import React, { ReactElement, useRef, FunctionComponent } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import BasicInput from '@empiriecom/module-components/FormControls/Input/BasicInput';
import Textarea from '@empiriecom/module-components/FormControls/Textarea';
import Checkbox from '@empiriecom/module-components/FormControls/Checkbox';
import ErrorMessage from '@empiriecom/module-components/FormControls/Input/ErrorMessage';
import { formTheme } from '@empiriecom/module-components/FormControls/theme';
import { ComponentThemeProvider } from '@empiriecom/module-components/ThemeProvider';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { RemoveTag } from '@/src/components/icons';
import { RatingFormField } from '@/config/default';
import { useCreateRatingContext } from '../CreateRatingContext';

const messages = defineMessages({
  advantageLabel: {
    defaultMessage: 'Vorteile',
    id: 'ratings.new-rating.form.advantage-label',
  },
  advantagePlaceholder: {
    defaultMessage: 'z.B. Weich',
    id: 'ratings.new-rating.form.advantage-placeholder',
  },
  cityLabel: {
    defaultMessage: 'Wohnort',
    id: 'ratings.new-rating.form.city-label',
  },
  cityPlaceholder: {
    defaultMessage: 'Dein Wohnort',
    id: 'ratings.new-rating.form.city-placeholder',
  },
  disadvantageLabel: {
    defaultMessage: 'Nachteile',
    id: 'ratings.new-rating.form.disadvantage-label',
  },
  disadvantagePlaceholder: {
    defaultMessage: 'z.B. zu warm',
    id: 'ratings.new-rating.form.disadvantage-placeholder',
  },
  headlineLabel: {
    defaultMessage: 'Überschrift',
    id: 'ratings.new-rating.form.headline-label',
  },
  headlinePlaceholder: {
    defaultMessage: 'Überschrift der Bewertung',
    id: 'ratings.new-rating.form.headline-placeholder',
  },
  nicknameLabel: {
    defaultMessage: 'Name*',
    id: 'ratings.new-rating.form.nickname-label',
  },
  nicknamePlaceholder: {
    defaultMessage: 'Dein Vorname oder Kürzel',
    id: 'ratings.new-rating.form.nickname-placeholder',
  },
  reviewLabel: {
    defaultMessage: 'Bewertung*',
    id: 'ratings.new-rating.form.review-label',
  },
  reviewPlaceholder: {
    defaultMessage: 'Was hat dir gefallen, was weniger?',
    id: 'ratings.new-rating.form.review-placeholder',
  },
});

const InputWrapper = styled.div<{ mobile: boolean }>`
  margin-bottom: ${({ mobile }) => (mobile ? '1.25rem;' : '1rem;')};
  &&:last-of-type {
    margin-bottom: 0;
  }
`;

const AdvantagesHint = styled.div`
  font-size: 0.75rem;
  margin: 2.75rem 0 0.75rem;
`;

const InputWithTags = styled.div`
  position: relative;
`;

const TagInput = styled(BasicInput)`
  padding-right: 2.75rem;
`;

const AddTagButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 2.25rem;
  right: 0;
  bottom: 0;
  border-left: ${({ theme }) => `1px solid ${theme.greyNormal.color.normal}`};
  line-height: 1.6;
  overflow: hidden;
  padding: 0.475rem 0;
  cursor: pointer;
`;

const ReviewTagsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-flow: row wrap;
  margin-top: 0.5rem;
`;

const ReviewTag = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-right: 0.25rem;
  padding: 0.5rem 0.75rem;
  background-color: #f1f2f3;
  border-radius: 1rem;
  overflow: hidden;
`;

const RemoveTagButton = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: #000000;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 0.75rem;
`;

const RemoveTagIcon = styled(RemoveTag)`
  fill: #f1f2f3;
`;

const TermsLink = styled.div`
  display: inline;
  text-decoration: underline;
`;

interface CreateRatingFormInputProps {
  field: RatingFormField;
}

export const CreateRatingFormInput: FunctionComponent<CreateRatingFormInputProps> = ({
  field,
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const headlineInputRef = useRef(null);
  const reviewInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const advantageInputRef = useRef(null);
  const disadvantageInputRef = useRef(null);
  const {
    getRatingHeadline,
    setRatingHeadline,
    getRatingReview,
    setRatingReview,
    getReviewerName,
    setReviewerName,
    getReviewerCity,
    setReviewerCity,
    pushAdvantageTag,
    removeAdvantageTag,
    getAdvantageTags,
    removeDisadvantageTag,
    pushDisadvantageTag,
    getDisadvantageTags,
    setTermsAccepted,
    getTermsAccepted,
    setCreationStep,
  } = useCreateRatingContext();
  const AddTagIcon = getIcon('Plus');
  const intl = useIntl();
  let input: JSX.Element;
  switch (field.name) {
    case 'headline':
      input = (
        <InputWrapper mobile={isMobile}>
          <BasicInput
            data-testid="rating-headline-input"
            label={intl.formatMessage(messages.headlineLabel)}
            placeholder={intl.formatMessage(messages.headlinePlaceholder)}
            validationRules={[
              {
                error: {
                  defaultMessage: 'Du musst eine Überschrift eingeben.',
                  description: undefined,
                  id: 'ratings.new-rating.review-headline-error',
                },
                required: false,
              },
            ]}
            inputRef={headlineInputRef}
            value={getRatingHeadline()}
            onChange={() => setRatingHeadline(headlineInputRef.current.value)}
          />
        </InputWrapper>
      );
      break;
    case 'name':
      input = (
        <InputWrapper mobile={isMobile}>
          <BasicInput
            data-testid="rating-name-input"
            label={intl.formatMessage(messages.nicknameLabel)}
            placeholder={intl.formatMessage(messages.nicknamePlaceholder)}
            validationRules={[
              {
                error: {
                  defaultMessage: 'Du musst einen Nicknamen eingeben.',
                  description: undefined,
                  id: 'ratings.new-rating.reviewer-name-error',
                },
                required: true,
              },
            ]}
            inputRef={nameInputRef}
            value={getReviewerName()}
            onChange={() => {
              setReviewerName(nameInputRef.current.value);
            }}
          />
        </InputWrapper>
      );
      break;
    case 'city':
      input = (
        <InputWrapper mobile={isMobile}>
          <BasicInput
            data-testid="rating-city-input"
            label={intl.formatMessage(messages.cityLabel)}
            placeholder={intl.formatMessage(messages.cityPlaceholder)}
            validationRules={[
              {
                error: {
                  defaultMessage: 'Du musst deinen Wohnort eingeben.',
                  description: undefined,
                  id: 'ratings.new-rating.reviewer-city-error',
                },
                required: false,
              },
            ]}
            inputRef={cityInputRef}
            value={getReviewerCity()}
            onChange={() => setReviewerCity(cityInputRef.current.value)}
          />
        </InputWrapper>
      );
      break;
    case 'review':
      input = (
        <InputWrapper mobile={isMobile} data-testid="rating-review-input-wrapper">
          <Textarea
            label={intl.formatMessage(messages.reviewLabel)}
            placeholder={intl.formatMessage(messages.reviewPlaceholder)}
            required
            inputRef={reviewInputRef}
            onChange={() => {
              setRatingReview(reviewInputRef.current.value);
            }}
          >
            {getRatingReview()}
          </Textarea>
        </InputWrapper>
      );
      break;
    case 'advantagesHint':
      input = (
        <AdvantagesHint>
          <FormattedMessage
            id="ratings.new-rating.advantages-hint"
            defaultMessage='Durch deine Erfahrung hilfst du anderen Kunden, sich für das richtige Produkt zu entscheiden. Füge Vor- und Nachteile über das "+" Icon hinzu.'
          />
        </AdvantagesHint>
      );
      break;
    case 'advantages':
      input = (
        <InputWrapper mobile={isMobile}>
          <InputWithTags>
            <TagInput
              data-testid="rating-advantage-input"
              label={intl.formatMessage(messages.advantageLabel)}
              placeholder={intl.formatMessage(messages.advantagePlaceholder)}
              validationRules={[
                {
                  error: {
                    defaultMessage: 'Du musst mindestens einen Vorteil angeben.',
                    description: undefined,
                    id: 'ratings.new-rating.review-advantages-error',
                  },
                  required: false,
                },
              ]}
              inputRef={advantageInputRef}
              onKeyUp={(event) => {
                if (event.keyCode === 13 && advantageInputRef.current.value !== '') {
                  event.preventDefault();
                  pushAdvantageTag(advantageInputRef.current.value);
                  advantageInputRef.current.value = '';
                }
              }}
            />
            <AddTagButton
              data-testid="rating-add-advantage"
              onClick={() => {
                if (advantageInputRef.current.value !== '') {
                  pushAdvantageTag(advantageInputRef.current.value);
                  advantageInputRef.current.value = '';
                }
              }}
            >
              <AddTagIcon size="1.25rem" />
            </AddTagButton>
          </InputWithTags>
          <ReviewTagsWrapper>
            {getAdvantageTags().length > 0 &&
              getAdvantageTags().map((tag, index) => (
                <ReviewTag key={tag.concat(index.toFixed())} data-testid="advantage-tag">
                  {tag}
                  <RemoveTagButton
                    onClick={() => removeAdvantageTag(tag)}
                    data-testid="remove-advantage-tag"
                  >
                    <RemoveTagIcon size="0.5rem" />
                  </RemoveTagButton>
                </ReviewTag>
              ))}
          </ReviewTagsWrapper>
        </InputWrapper>
      );
      break;
    case 'disadvantages':
      input = (
        <InputWrapper key={field.name} mobile={isMobile}>
          <InputWithTags>
            <TagInput
              data-testid="rating-disadvantage-input"
              label={intl.formatMessage(messages.disadvantageLabel)}
              placeholder={intl.formatMessage(messages.disadvantagePlaceholder)}
              validationRules={[
                {
                  error: {
                    defaultMessage: 'Du musst mindestens einen Nachteil angeben.',
                    description: undefined,
                    id: 'ratings.new-rating.review-disadvantages-error',
                  },
                  required: false,
                },
              ]}
              inputRef={disadvantageInputRef}
              onKeyUp={(event) => {
                if (event.keyCode === 13 && disadvantageInputRef.current.value !== '') {
                  event.preventDefault();
                  pushDisadvantageTag(disadvantageInputRef.current.value);
                  disadvantageInputRef.current.value = '';
                }
              }}
            />
            <AddTagButton
              data-testid="rating-add-disadvantage"
              onClick={() => {
                if (disadvantageInputRef.current.value !== '') {
                  pushDisadvantageTag(disadvantageInputRef.current.value);
                  disadvantageInputRef.current.value = '';
                }
              }}
            >
              <AddTagIcon size="1.25rem" />
            </AddTagButton>
          </InputWithTags>
          <ReviewTagsWrapper>
            {getDisadvantageTags().length > 0 &&
              getDisadvantageTags().map((tag, index) => (
                <ReviewTag key={tag.concat(index.toFixed())} data-testid="disadvantage-tag">
                  {tag}
                  <RemoveTagButton
                    onClick={() => removeDisadvantageTag(tag)}
                    data-testid="remove-disadvantage-tag"
                  >
                    <RemoveTagIcon size="0.5rem" />
                  </RemoveTagButton>
                </ReviewTag>
              ))}
          </ReviewTagsWrapper>
        </InputWrapper>
      );
      break;
    case 'terms':
      input = (
        <InputWrapper mobile={isMobile} data-testid="rating-terms-input">
          <Checkbox
            label={
              <>
                <FormattedMessage
                  id="ratings.new-rating.terms-link.pre"
                  defaultMessage="Ich habe die"
                />
                <span> </span>
                <TermsLink onClick={() => setCreationStep('TERMS')} data-testid="rating-terms-link">
                  <FormattedMessage
                    id="ratings.new-rating.terms-link.label"
                    defaultMessage="Nutzungsbedingungen"
                  />
                </TermsLink>
                <span> </span>
                <FormattedMessage
                  id="ratings.new-rating.terms-link.post"
                  defaultMessage="gelesen und erkläre mich damit einverstanden.*"
                />
              </>
            }
            onChange={(event) => {
              setTermsAccepted(event.target.checked);
            }}
            error={getTermsAccepted() !== null && !getTermsAccepted()}
          />
          {getTermsAccepted() !== null && !getTermsAccepted() && (
            <ComponentThemeProvider theme={formTheme}>
              <ErrorMessage
                error={
                  <FormattedMessage
                    id="ratings.new-rating.terms-error"
                    defaultMessage="Bitte akzeptiere unsere Nutzungsbedingungen."
                  />
                }
              />
            </ComponentThemeProvider>
          )}
        </InputWrapper>
      );
      break;
    default:
      input = null;
      break;
  }
  return input;
};

export default CreateRatingFormInput;
